import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Compass, Sparkles, CheckCircle2, Rocket, Loader2, ArrowRight, ArrowLeft,
  Brain, Network, Zap, ShieldCheck, Workflow, Search, Hexagon,
  LayoutGrid, GitBranch, Share2, Coins, AlertTriangle,
  UserCheck, FileJson, FileText, Activity, Database, Image as ImageIcon,
  Layers, Shield, Landmark, HeartPulse, FlaskConical, Scale, Calculator,
  Factory, ShoppingCart, Radio, Building, Truck, HardHat, GraduationCap,
  Globe, Component, ChevronDown, Check, Plug, Copy, Pencil, Trash2, X,
  BriefcaseBusiness, LockKeyhole, type LucideIcon
} from "lucide-react";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, EmptyState, StatusPill } from "@/components/common";
import { OntologyMiniGraph } from "@/components/OntologyMiniGraph";
import { OntologyGraphExplorer, type OntologyGraph } from "@/components/OntologyGraphExplorer";
import { useApp } from "@/lib/store";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";
import { activeAnswers, hasAnswer, requiredComplete, visibleQuestions, type StudioAnswers, type StudioQuestion as Question } from "@/lib/studioInterview";
import { StudioDagEditor } from "@/components/StudioDagEditor";

interface Recommendation {
  decision_path?: string; rationale?: string; confidence?: number;
  caveat?: string; is_directional?: boolean;
  scores?: Record<string, number>;
}
interface OntologyEntity {
  name: string;
  description?: string;
  properties?: Record<string, { type?: string } | unknown>;
}
interface OntologyRelationship {
  name: string; source: string; target: string;
  cardinality?: string; description?: string;
}
interface OntologyBlueprint {
  semver?: string;
  domain?: string;
  entity_types?: OntologyEntity[];
  relationships?: OntologyRelationship[];
  stats?: { entities?: number; relationships?: number; properties?: number };
}
interface Blueprint {
  requirements?: { status: string; answers: StudioAnswers };
  ontology?: OntologyBlueprint;
  retrieval?: { name?: string; modes?: { key: string }[]; top_k?: number };
  dag?: { name?: string; industry?: string; template_only?: boolean; processors?: string[] };
  agents?: { name?: string; model?: string; enabled?: boolean };
  governance?: { requires_approval?: boolean; policy_note?: string };
  evals?: { dataset_name?: string };
}
interface Session {
  id: string; name: string; status: string;
  session_type?: "architecture" | "composer";
  answers?: StudioAnswers;
  recommendation?: Recommendation;
  blueprint?: Blueprint;
  deployed_refs?: Record<string, string | null>;
}

const PATH_META: Record<string, { Icon: typeof Brain; label: string; blurb: string }> = {
  knowledge: { Icon: Brain, label: "Knowledge", blurb: "Retrieval-first Q&A over your documents." },
  graph: { Icon: Network, label: "Graph", blurb: "Relationship-first, multi-hop reasoning over an entity graph." },
  action: { Icon: Zap, label: "Action", blurb: "Closed-loop, event-triggered automation." },
};

function enhanceOption(label: string) {
  const lower = label.toLowerCase();
  
  if (lower.includes("answer questions")) return { Icon: Brain, short: "Knowledge Q&A" };
  if (lower.includes("relationships")) return { Icon: Network, short: "Graph Reasoning" };
  if (lower.includes("automate")) return { Icon: Zap, short: "Automation" };
  if (lower.includes("expert")) return { Icon: UserCheck, short: "Decision Support" };
  if (lower.includes("extract")) return { Icon: FileJson, short: "Data Extraction" };
  if (lower.includes("risk") || lower.includes("compliance")) return { Icon: ShieldCheck, short: "Risk & Compliance" };

  if (lower.includes("documents") || lower.includes("pdf")) return { Icon: FileText, short: "Documents & PDFs" };
  if (lower.includes("connected")) return { Icon: Share2, short: "Connected Records" };
  if (lower.includes("streams") || lower.includes("events")) return { Icon: Activity, short: "Streams & Events" };
  if (lower.includes("databases") || lower.includes("warehouse")) return { Icon: Database, short: "Databases" };
  if (lower.includes("audio") || lower.includes("video")) return { Icon: ImageIcon, short: "Media & Scans" };
  if (lower.includes("mix")) return { Icon: Layers, short: "Mixed Data" };

  if (lower.includes("insurance")) return { Icon: Shield, short: "Insurance" };
  if (lower.includes("banking") || lower.includes("capital")) return { Icon: Landmark, short: "Banking & Capital" };
  if (lower.includes("healthcare")) return { Icon: HeartPulse, short: "Healthcare" };
  if (lower.includes("pharma") || lower.includes("life sciences")) return { Icon: FlaskConical, short: "Pharma & Life Sci" };
  if (lower.includes("legal")) return { Icon: Scale, short: "Legal" };
  if (lower.includes("tax") || lower.includes("accounting")) return { Icon: Calculator, short: "Accounting" };
  if (lower.includes("manufacturing")) return { Icon: Factory, short: "Manufacturing" };
  if (lower.includes("retail") || lower.includes("commerce")) return { Icon: ShoppingCart, short: "Retail & E-comm" };
  if (lower.includes("energy") || lower.includes("utilities")) return { Icon: Zap, short: "Energy & Utilities" };
  if (lower.includes("telecom") || lower.includes("media")) return { Icon: Radio, short: "Telecom & Media" };
  if (lower.includes("public sector") || lower.includes("government")) return { Icon: Building, short: "Government" };
  if (lower.includes("logistics") || lower.includes("supply")) return { Icon: Truck, short: "Logistics" };
  if (lower.includes("real estate") || lower.includes("construction")) return { Icon: HardHat, short: "Real Estate" };
  if (lower.includes("education")) return { Icon: GraduationCap, short: "Education" };
  if (lower.includes("general") || lower.includes("other")) return { Icon: Globe, short: "General / Other" };

  return { Icon: Component, short: label };
}

export function Studio() {
  const ws = useApp((s) => s.workspaceId);
  const { canEdit, canAdmin, isLoading: roleLoading, error: roleError } = useWorkspacePermissions();
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [answers, setAnswers] = useState<StudioAnswers>({});
  const [stepIdx, setStepIdx] = useState(0);
  const [expandedQ, setExpandedQ] = useState<string | null>(null);
  const [sessionSearch, setSessionSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const sessions = useQuery<{ results: Session[] }>({
    queryKey: ["studio-sessions", ws],
    queryFn: () => rawFetch(`/studio/sessions/?page_size=50${ws ? `&workspace=${ws}` : ""}`),
    enabled: !!ws,
  });

  const questions = useQuery<{ questions: Question[] }>({
    queryKey: ["studio-questions"],
    queryFn: () => rawFetch(`/studio/sessions/questions/`),
  });

  const active = useMemo(
    () => sessions.data?.results?.find((s) => s.id === activeId) ?? null,
    [sessions.data, activeId],
  );
  const filteredSessions = useMemo(() => {
    const query = sessionSearch.trim().toLowerCase();
    const items = sessions.data?.results ?? [];
    return query ? items.filter((session) => session.name.toLowerCase().includes(query)) : items;
  }, [sessions.data, sessionSearch]);

  const steps = useMemo<Question[][]>(() => {
    const qs = visibleQuestions(questions.data?.questions ?? [], answers);
    if (qs.length === 0) return [];
    const grouped = qs.some((q) => q.group);
    if (!grouped) return qs.map((q) => [q]);
    const order: string[] = [];
    const byGroup = new Map<string, Question[]>();
    for (const q of qs) {
      const g = q.group ?? "More";
      if (!byGroup.has(g)) { byGroup.set(g, []); order.push(g); }
      byGroup.get(g)!.push(q);
    }
    return order.map((g) => byGroup.get(g)!);
  }, [questions.data, answers]);

  useEffect(() => {
    const currentQs = steps[stepIdx] ?? [];
    if (currentQs.length > 0 && !currentQs.some(q => q.id === expandedQ)) {
      const firstUnanswered = currentQs.find((q) => !hasAnswer(answers[q.id]));
      setExpandedQ(firstUnanswered ? firstUnanswered.id : currentQs[0].id);
    }
  }, [stepIdx, steps, answers, expandedQ]);

  useEffect(() => { 
    setStepIdx(0); 
    setExpandedQ(null);
  }, [activeId]);

  const handleAnswer = (qId: string, val: string, stepQuestions: Question[]) => {
    const question = stepQuestions.find(q => q.id === qId);
    setAnswers((a) => {
      const previous = Array.isArray(a[qId]) ? a[qId] as string[] : [];
      const value = question?.multiple
        ? previous.includes(val) ? previous.filter(v => v !== val) : [...previous, val]
        : val;
      return activeAnswers(questions.data?.questions ?? [], { ...a, [qId]: value });
    });
    if (!question?.multiple) {
      const currIdx = stepQuestions.findIndex(q => q.id === qId);
      if (currIdx >= 0 && currIdx < stepQuestions.length - 1) {
        setExpandedQ(stepQuestions[currIdx + 1].id);
      }
    }
  };

  const create = useMutation({
    mutationFn: () => rawFetch<Session>(`/studio/sessions/`, {
      method: "POST",
      body: JSON.stringify({ workspace: ws, name: name || "New architecture" }),
    }),
    onSuccess: (s) => {
      setActiveId(s.id);
      setAnswers({});
      setName("");
      qc.invalidateQueries({ queryKey: ["studio-sessions", ws] });
    },
  });

  const rename = useMutation({
    mutationFn: ({ id, sessionName }: { id: string; sessionName: string }) => rawFetch<Session>(`/studio/sessions/${id}/`, {
      method: "PATCH",
      body: JSON.stringify({ name: sessionName }),
    }),
    onSuccess: () => {
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ["studio-sessions", ws] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => rawFetch<void>(`/studio/sessions/${id}/`, { method: "DELETE" }),
    onSuccess: (_, id) => {
      if (activeId === id) setActiveId(null);
      setDeletingId(null);
      qc.invalidateQueries({ queryKey: ["studio-sessions", ws] });
    },
  });

  const recommend = useMutation({
    mutationFn: (id: string) => rawFetch<Session>(`/studio/sessions/${id}/recommend/`, {
      method: "POST",
      body: JSON.stringify({ answers: activeAnswers(questions.data?.questions ?? [], answers) }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["studio-sessions", ws] }),
  });

  const approve = useMutation({
    mutationFn: (id: string) => rawFetch<Session>(`/studio/sessions/${id}/approve/`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["studio-sessions", ws] }),
  });

  const deploy = useMutation({
    mutationFn: (id: string) => rawFetch<Session>(`/studio/sessions/${id}/deploy/`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["studio-sessions", ws] }),
  });

  if (!ws) {
    return (
      <>
        <PageHeader title="Architecture Studio" subtitle="Describe a business problem; Ontoligent designs the architecture." />
        <EmptyState title="Select a workspace first." hint="Use the workspace switcher in the header." />
      </>
    );
  }

  const allAnswered = !!questions.data?.questions.length && requiredComplete(questions.data.questions, answers);
  const currentStepQs = steps[stepIdx] ?? [];

  return (
    <>
      <PageHeader
        title="Enterprise Architecture Studio"
        subtitle="Governed solution design from business discovery through controlled deployment."
        help="Answer a business-discovery interview to get a reviewable blueprint. The pipeline recommendation can be edited and saved as an immutable revision; Studio's separate deployment action materializes ontology, retrieval and agent rows."
        learnMore={{ to: "/howto", hash: "studio", label: "Studio walkthrough" }}
      />

      {!roleLoading && !canEdit && (
        <div className="card mb-4 text-xs text-muted" role="status">
          {roleError ? "Unable to verify your workspace role. Editing is unavailable." :
            "Studio is read-only for your role. An editor can author; an admin can approve and deploy."}
        </div>
      )}

      <div className="mb-4 grid border-y border-border bg-panel/40 sm:grid-cols-3">
        <div className="flex items-center gap-3 px-4 py-3"><BriefcaseBusiness size={18} className="text-accent" /><div><div className="text-xs font-semibold">Architecture workspace</div><div className="text-[11px] text-muted">Persistent, managed design sessions</div></div></div>
        <div className="flex items-center gap-3 border-border px-4 py-3 sm:border-x"><ShieldCheck size={18} className="text-good" /><div><div className="text-xs font-semibold">Approval control</div><div className="text-[11px] text-muted">Review before deployment</div></div></div>
        <div className="flex items-center gap-3 px-4 py-3"><LockKeyhole size={18} className="text-accent" /><div><div className="text-xs font-semibold">Workspace isolation</div><div className="text-[11px] text-muted">Tenant-scoped architecture assets</div></div></div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr] min-w-0">
        {/* --- Sessions rail --- */}
        <div className="space-y-3 min-w-0">
          <div className="card">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted">
              New session
            </div>
            <input
              className="input mb-3" placeholder="Name (e.g. Claims triage)"
              value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit}
            />
            <button className="btn-primary w-full" disabled={!canEdit || create.isPending}
                    onClick={() => create.mutate()}>
              {create.isPending ? <Loader2 className="mr-1 inline animate-spin" size={14} /> : <Compass className="mr-1 inline" size={14} />}
              Start interview
            </button>
            {create.isError && <div className="mt-2"><ErrorBox error={create.error} /></div>}
          </div>

          <div className="card p-0">
            <div className="border-b border-border p-3">
              <div className="mb-2 flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-muted">
                <span>Sessions</span><span>{sessions.data?.results?.length ?? 0}</span>
              </div>
              <label className="relative block">
                <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
                <input className="input h-8 pl-8 text-xs" value={sessionSearch} onChange={(event) => setSessionSearch(event.target.value)} placeholder="Filter sessions" />
              </label>
            </div>
            {sessions.isLoading ? (
              <div className="p-4 text-sm text-muted">Loading…</div>
            ) : filteredSessions.length === 0 ? (
              <div className="p-4 text-sm text-muted">{sessionSearch ? "No matching sessions." : "No sessions yet."}</div>
            ) : (
              <ul className="divide-y divide-border">
                {filteredSessions.map((s) => (
                  <li key={s.id}>
                    {editingId === s.id ? (
                      <form className="flex gap-1 p-2" onSubmit={(event) => { event.preventDefault(); if (editingName.trim()) rename.mutate({ id: s.id, sessionName: editingName.trim() }); }}>
                        <input autoFocus className="input h-8 min-w-0 flex-1 text-xs" value={editingName} onChange={(event) => setEditingName(event.target.value)} aria-label="Session name" />
                        <button className="btn h-8 w-8 p-0" type="submit" title="Save name"><Check size={14} /></button>
                        <button className="btn h-8 w-8 p-0" type="button" onClick={() => setEditingId(null)} title="Cancel"><X size={14} /></button>
                      </form>
                    ) : deletingId === s.id ? (
                      <div className="p-3 text-xs">
                        <p className="mb-2 text-text">Delete “{s.name}” permanently?</p>
                        <div className="flex gap-2"><button className="btn border-bad/40 text-bad" onClick={() => remove.mutate(s.id)} disabled={remove.isPending}>Delete</button><button className="btn" onClick={() => setDeletingId(null)}>Cancel</button></div>
                      </div>
                    ) : (
                      <div className={`group flex items-center border-l-2 pr-2 ${s.id === activeId ? "border-accent bg-accent/5" : "border-transparent"}`}>
                        {s.session_type === "composer" ? (
                          <a className="min-w-0 flex-1 px-3 py-3 text-left text-sm hover:bg-border/30" href={`/studio/composer?session=${s.id}`}>
                            <span className="mb-1 block truncate font-medium">{s.name}</span><span className="pill mr-1 border-accent/30 text-accent">Composer</span><StatusPill status={s.status} />
                          </a>
                        ) : (
                          <button className="min-w-0 flex-1 px-3 py-3 text-left text-sm hover:bg-border/30" onClick={() => { setActiveId(s.id); setAnswers(s.answers ?? {}); }}>
                            <span className="mb-1 block truncate font-medium">{s.name}</span><StatusPill status={s.status} />
                          </button>
                        )}
                        {canEdit && <button className="rounded p-1.5 text-muted hover:bg-border/50 hover:text-text" title="Rename session" onClick={() => { setEditingId(s.id); setEditingName(s.name); }}><Pencil size={14} /></button>}
                        {canEdit && <button className="rounded p-1.5 text-muted hover:bg-bad/10 hover:text-bad" title="Delete session" onClick={() => setDeletingId(s.id)}><Trash2 size={14} /></button>}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* --- Active session canvas --- */}
        {/* min-w-0 on the grid child prevents Flex/Grid blowouts from horizontally scrolling components inside it */}
        <div className="space-y-4 min-w-0">
          {!active ? (
            <div className="card flex min-h-[400px] flex-col justify-center border-t-2 border-t-accent p-8">
              <div className="max-w-2xl"><Hexagon size={36} className="mb-5 text-accent" /><div className="mb-2 text-xs font-semibold uppercase tracking-wider text-accent">Architecture control plane</div><h2 className="text-2xl font-semibold text-text">Design systems with an auditable path to deployment.</h2><p className="mt-3 max-w-xl text-sm leading-relaxed text-muted">Open an existing engagement or start a governed discovery session. Studio records decisions, generates the semantic and operational blueprint, and requires explicit approval before materialization.</p></div>
            </div>
          ) : (
            <>
              {/* Step 1 — interview */}
              <section className="card shadow-sm">
                <p className="mb-4 text-sm text-muted">This interview records requirements and proposes a blueprint. It does not configure SSO, retention, integrations or residency, or certify compliance.</p>
                <div className="mb-6 flex items-center justify-between border-b border-border pb-4">
                  <h2 className="flex items-center gap-3 text-base font-semibold">
                    <span className="grid h-7 w-7 place-items-center rounded-lg bg-accent text-xs font-bold text-bg shadow-sm">1</span>
                    Business Discovery
                  </h2>
                  {steps.length > 0 && (
                    <span className="text-xs font-medium text-muted">
                      Phase {Math.min(stepIdx + 1, steps.length)} / {steps.length}
                    </span>
                  )}
                </div>

                {steps.length > 1 && (
                  <div className="mb-6 flex gap-2">
                    {steps.map((grp, i) => {
                      const done = requiredComplete(grp, answers);
                      const cur = i === stepIdx;
                      return (
                        <button
                          key={i}
                          onClick={() => setStepIdx(i)}
                          aria-label={`Go to step ${i + 1}`}
                          className={`h-2 flex-1 rounded-full transition-all duration-300 ${
                            cur ? "bg-accent shadow-sm" : done ? "bg-accent/30" : "bg-border"
                          }`}
                        />
                      );
                    })}
                  </div>
                )}

                <div className="space-y-2">
                  {currentStepQs.map((q) => {
                    const isExpanded = expandedQ === q.id;
                    const answered = hasAnswer(answers[q.id]);
                    
                    return (
                      <div key={q.id} data-question={q.id} className="overflow-hidden rounded-xl border border-border bg-panel/30">
                        <button 
                          className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-panel/50"
                          onClick={() => setExpandedQ(isExpanded ? null : q.id)}
                          aria-expanded={isExpanded}
                          aria-controls={`question-${q.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border text-[10px] transition-colors ${
                              answered ? "border-good bg-good/10 text-good" : "border-border text-muted"
                            }`}>
                              {answered ? <Check size={12} /> : null}
                            </span>
                            <span className={`text-sm font-medium ${isExpanded ? "text-text" : "text-muted"}`}>
                              {q.prompt}
                              {q.required === false && <span className="ml-2 text-xs text-muted">Optional</span>}
                            </span>
                          </div>
                          <ChevronDown size={16} className={`text-muted transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} />
                        </button>
                        
                        <div id={`question-${q.id}`} hidden={!isExpanded}>
                          <div className="border-t border-border/50 bg-bg/50 p-5">
                            {q.help && <div className="mb-4 text-[11px] font-medium text-muted">{q.help}</div>}
                            {q.multiple && <p className="mb-3 text-xs text-muted">Select all that apply; select again to remove.</p>}
                            {q.required === false && answered && <button className="btn mb-3" disabled={!canEdit} onClick={() => setAnswers(a => { const next = { ...a }; delete next[q.id]; return next; })}>Clear selection</button>}
                            
                            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                              {q.options.map((o) => {
                                const { Icon, short } = enhanceOption(o.label);
                                const value = answers[q.id];
                                const isSelected = Array.isArray(value) ? value.includes(o.value) : value === o.value;
                                
                                return (
                                  <button
                                    key={o.value}
                                    disabled={!canEdit}
                                    title={o.label}
                                    aria-label={o.label}
                                    aria-pressed={isSelected}
                                    className={`group relative flex flex-col items-start gap-3 overflow-hidden rounded-xl border p-4 text-left transition-all duration-300 ${
                                      isSelected 
                                        ? "border-accent bg-gradient-to-br from-accent/10 to-accent/5 shadow-md shadow-accent/5 ring-1 ring-accent" 
                                        : "border-border bg-panel/40 hover:-translate-y-0.5 hover:border-accent/40 hover:bg-panel/80 hover:shadow-sm"
                                    }`}
                                    onClick={() => handleAnswer(q.id, o.value, currentStepQs)}
                                  >
                                    {o.icon_svg ? (
                                      <div 
                                        className={`relative z-10 h-6 w-6 transition-colors duration-200 ${isSelected ? "text-accent" : "text-muted"}`} 
                                        dangerouslySetInnerHTML={{ __html: o.icon_svg }} 
                                      />
                                    ) : (
                                      <Icon className={`relative z-10 h-6 w-6 transition-colors duration-200 ${isSelected ? "text-accent" : "text-muted"}`} />
                                    )}
                                    
                                    <span className={`relative z-10 text-[12px] font-semibold leading-snug ${isSelected ? "text-accent" : "text-text"}`}>
                                      {o.icon_svg || q.required === false ? o.label : short}
                                    </span>
                                    
                                    {!o.icon_svg && (
                                      <Icon className={`absolute -bottom-4 -right-4 z-0 h-24 w-24 opacity-[0.02] transition-transform duration-300 group-hover:scale-110 group-hover:opacity-[0.05] ${isSelected ? "text-accent opacity-[0.06]" : ""}`} />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-4">
                  <button
                    className="btn"
                    disabled={stepIdx === 0}
                    onClick={() => {
                      const newIdx = Math.max(0, stepIdx - 1);
                      setStepIdx(newIdx);
                      setExpandedQ(steps[newIdx]?.[0]?.id || null);
                    }}
                  >
                    <ArrowLeft className="mr-1 inline" size={14} /> Back
                  </button>

                  {stepIdx < steps.length - 1 ? (
                    <button
                      className="btn-primary"
                      disabled={!requiredComplete(steps[stepIdx] ?? [], answers)}
                      onClick={() => {
                        const newIdx = Math.min(steps.length - 1, stepIdx + 1);
                        setStepIdx(newIdx);
                        setExpandedQ(steps[newIdx]?.[0]?.id || null);
                      }}
                    >
                      Next Phase <ArrowRight className="ml-1 inline" size={14} />
                    </button>
                  ) : (
                    <button className="btn-primary px-6" disabled={!canEdit || !allAnswered || recommend.isPending}
                            onClick={() => recommend.mutate(active.id)}>
                      {recommend.isPending ? <Loader2 className="mr-2 inline animate-spin" size={16} /> : <Sparkles className="mr-2 inline" size={16} />}
                      Generate Blueprint
                    </button>
                  )}
                </div>
                {stepIdx === steps.length - 1 && !allAnswered && (
                  <p className="mt-3 text-right text-xs text-muted">Complete the visible required questions. Optional requirements can be skipped.</p>
                )}
                {recommend.isError && <div className="mt-4"><ErrorBox error={recommend.error} /></div>}
              </section>

              {/* Step 2 — recommendation + tabbed blueprint */}
              {active.recommendation?.decision_path && (
                <section className="card shadow-sm min-w-0">
                  <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
                    <h2 className="flex items-center gap-3 text-base font-semibold">
                      <span className="grid h-7 w-7 place-items-center rounded-lg bg-accent text-xs font-bold text-bg shadow-sm">2</span>
                      Recommended Architecture
                    </h2>
                    {active.status === "recommended" && canAdmin && (
                      <button className="btn-primary" disabled={approve.isPending}
                              onClick={() => approve.mutate(active.id)}>
                        {approve.isPending ? <Loader2 className="mr-1 inline animate-spin" size={14} /> : <CheckCircle2 className="mr-1 inline" size={14} />}
                        Approve Blueprint
                      </button>
                    )}
                  </div>
                  
                  <RecommendationCard rec={active.recommendation} />
                  {active.blueprint?.requirements && (
                    <section className="mt-5 border-t border-border pt-4" aria-label="Recorded requirements">
                      <h3 className="font-semibold">Recorded requirements — not configured</h3>
                      <p className="mt-1 text-sm text-muted">These choices need implementation review; generating or approving a blueprint does not apply them.</p>
                      <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                        {Object.entries(active.blueprint.requirements.answers).map(([key, value]) => {
                          const question = questions.data?.questions.find(q => q.id === key);
                          const values = Array.isArray(value) ? value : [value];
                          return <div key={key}><dt className="text-xs text-muted">{question?.prompt ?? key}</dt><dd className="mt-1 text-sm">{values.map(v => question?.options.find(o => o.value === v)?.label ?? v).join("; ")}</dd></div>;
                        })}
                      </dl>
                      {!Object.keys(active.blueprint.requirements.answers).length && <p className="mt-2 text-sm text-muted">No optional requirements recorded.</p>}
                    </section>
                  )}
                  <div className="mt-6 min-w-0">
                    <BlueprintTabs bp={active.blueprint ?? {}} rec={active.recommendation}
                      sessionId={active.id} sessionName={active.name} workspace={ws} canEdit={canEdit} />
                  </div>
                  {approve.isError && <div className="mt-4"><ErrorBox error={approve.error} /></div>}
                </section>
              )}

              {/* Step 3 — deploy */}
              {(active.status === "approved" || active.status === "deployed") && (
                <section className="card shadow-sm border-good/30 bg-good/5">
                  <h2 className="mb-4 flex items-center gap-3 text-base font-semibold">
                    <span className="grid h-7 w-7 place-items-center rounded-lg bg-good text-xs font-bold text-bg shadow-sm">3</span>
                    Deployment Status
                  </h2>
                  {active.status === "approved" ? (
                    <div className="flex flex-col gap-4 rounded-xl border border-border bg-panel p-5 md:flex-row md:items-center md:justify-between">
                      <p className="text-sm text-text max-w-lg">
                        Blueprint approved. This action materializes ontology, retrieval and agent rows.
                        Save and run the pipeline separately from the Pipeline DAG tab.
                      </p>
                      <button className="btn-primary shrink-0" disabled={!canAdmin || deploy.isPending}
                              onClick={() => deploy.mutate(active.id)}>
                        {deploy.isPending ? <Loader2 className="mr-2 inline animate-spin" size={16} /> : <Rocket className="mr-2 inline" size={16} />}
                        Materialize Blueprint
                      </button>
                      {deploy.isError && <div className="mt-4 w-full"><ErrorBox error={deploy.error} /></div>}
                    </div>
                  ) : (
                    <DeployedRefs refs={active.deployed_refs ?? {}} sessionId={active.id} />
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function RecommendationCard({ rec }: { rec: Recommendation }) {
  const meta = PATH_META[rec.decision_path ?? ""] ?? PATH_META.knowledge;
  const Icon = meta.Icon;
  const pct = typeof rec.confidence === "number" ? Math.round(rec.confidence * 100) : null;
  const tone =
    pct === null ? "border-border bg-border/30 text-muted" :
    pct >= 67 ? "border-good/30 bg-good/10 text-good" :
    pct >= 50 ? "border-accent/30 bg-accent/10 text-accent" :
                "border-warn/30 bg-warn/10 text-warn";
  const caveat = rec.caveat
    ?? (pct !== null
      ? `This is a ${pct}% directional match — a strong starting point, not a final blueprint. Expect a Redpill architect (or your own team) to refine it; on the free tier the blueprint ships as-is, without expert review.`
      : undefined);
  return (
    <div className="rounded-xl border border-accent/20 bg-accent/5 p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-accent/15 text-accent shadow-sm">
          <Icon size={24} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3 text-base font-semibold">
            <span>{meta.label} Path</span>
            {pct !== null && (
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${tone}`}>
                <AlertTriangle size={12} /> ~{pct}% Match
              </span>
            )}
          </div>
          <div className="mt-1 text-sm text-text/80">{meta.blurb}</div>
          {rec.rationale && <p className="mt-3 text-[13px] leading-relaxed text-muted"><strong className="text-text">Rationale:</strong> {rec.rationale}</p>}
        </div>
      </div>
      {caveat && (
        <div className="mt-4 flex gap-2 rounded-lg border border-warn/25 bg-warn/10 p-3 text-xs leading-relaxed text-warn/90">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>{caveat}</span>
        </div>
      )}
    </div>
  );
}

// --- Ontology blueprint view ----------------------------------------------
function CardinalityBadge({ value }: { value?: string }) {
  const v = value ?? "N-N";
  const tone =
    v === "1-1" ? "bg-good/15 text-good border-good/30" :
    v === "1-N" ? "bg-accent/15 text-accent border-accent/30" :
                  "bg-warn/15 text-warn border-warn/30";
  return (
    <span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] ${tone}`}>{v}</span>
  );
}

function OntologyBlueprintView({ ontology }: { ontology?: OntologyBlueprint }) {
  const entities = ontology?.entity_types ?? [];
  const relationships = ontology?.relationships ?? [];

  if (entities.length === 0) {
    return <TabPlaceholder text="No entity types in this blueprint yet." />;
  }

  const stats = ontology?.stats ?? {
    entities: entities.length,
    relationships: relationships.length,
    properties: entities.reduce((n, e) => n + Object.keys(e.properties ?? {}).length, 0),
  };

  const graph = {
    nodes: entities.map((e) => ({ id: e.name, label: e.name })),
    edges: relationships.map((r, i) => ({
      id: `r${i}`, label: r.name, source: r.source, target: r.target,
    })),
  };

  return (
    <div className="w-full space-y-6 min-w-0">
      <div className="flex flex-wrap items-end justify-between gap-4 rounded-xl border border-border bg-panel p-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-base font-semibold">
            <Hexagon size={18} className="text-accent shrink-0" />
            <span className="truncate">Semantic Model</span>
            {ontology?.domain && (
              <span className="ml-2 rounded-md border border-border bg-bg px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-muted">
                {ontology.domain.replace(/_/g, " ")}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-muted">
            Typed entities, attributes and relationships mapping the business domain. Version {ontology?.semver ?? "draft"}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { label: "Entities", value: stats.entities ?? entities.length },
            { label: "Relationships", value: stats.relationships ?? relationships.length },
            { label: "Attributes", value: stats.properties ?? 0 },
          ].map((s) => (
            <div key={s.label} className="flex flex-1 flex-col items-center justify-center rounded-lg border border-border bg-bg px-3 py-2 sm:px-4">
              <div className="text-lg font-bold leading-none text-text sm:text-xl">{s.value}</div>
              <div className="mt-1 text-[9px] font-bold uppercase tracking-wider text-muted sm:text-[10px]">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <OntologyMiniGraph graph={graph} height={400} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[3fr_2fr]">
        <div className="min-w-0">
          <div className="mb-3 text-[11px] font-bold uppercase tracking-wider text-muted border-b border-border pb-2">Registered Entities</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {entities.map((e) => {
              const props = Object.entries(e.properties ?? {});
              return (
                <div key={e.name} className="min-w-0 rounded-xl border border-border bg-panel p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md border border-accent/25 bg-accent/10 text-accent">
                      <Hexagon size={12} />
                    </span>
                    <span className="truncate text-sm font-bold">{e.name}</span>
                  </div>
                  {e.description && (
                    <p className="mb-3 text-[11px] leading-relaxed text-muted line-clamp-3">{e.description}</p>
                  )}
                  {props.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {props.map(([key, def]) => {
                        const t = (def as { type?: string })?.type;
                        return (
                          <span key={key}
                            className="truncate rounded border border-border bg-bg px-2 py-0.5 font-mono text-[10px] text-muted max-w-full">
                            {key}{t ? <span className="text-accent/70">:{t}</span> : null}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="min-w-0">
          <div className="mb-3 text-[11px] font-bold uppercase tracking-wider text-muted border-b border-border pb-2">Topology</div>
          {relationships.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-panel p-6 text-center text-[11px] text-muted">
              No relationships defined yet.
            </div>
          ) : (
            <div className="space-y-2">
              {relationships.map((r, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-panel px-3 py-2 text-xs shadow-sm">
                  <span className="font-semibold text-text">{r.source}</span>
                  <ArrowRight size={12} className="shrink-0 text-muted" />
                  <span className="truncate font-mono text-[10px] font-medium text-accent max-w-[80px] sm:max-w-[120px]" title={r.name}>{r.name}</span>
                  <ArrowRight size={12} className="shrink-0 text-muted" />
                  <span className="font-semibold text-text">{r.target}</span>
                  <span className="ml-auto shrink-0"><CardinalityBadge value={r.cardinality} /></span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BlueprintCanvas({ bp, rec }: { bp: Blueprint; rec: Recommendation }) {
  const path = rec.decision_path ?? "knowledge";
  const agentDisabled = bp.agents?.enabled === false;

  const nodes = [
    {
      key: "sources", Icon: Workflow, title: "Sources", body: "Your documents & systems",
      why: "Connectors and uploads feed the pipeline; nothing is modelled until it has a source.",
    },
    {
      key: "ontology", Icon: Hexagon, title: "Ontology",
      body: `${bp.ontology?.entity_types?.length ?? 0} entity types · ${bp.ontology?.semver ?? "draft"}`,
      detail: (bp.ontology?.entity_types ?? []).map((e) => e.name).join(", "),
      why: `Provides shared vocabulary so extraction and retrieval agree on semantic structure.`,
    },
    {
      key: "pipeline", Icon: GitBranch, title: "Pipeline", body: bp.dag?.name ?? "Ingestion DAG",
      detail: (bp.dag?.processors ?? []).join(" → "),
      why: "Recommended processing sequence. Review and save a runtime pipeline separately.",
    },
    {
      key: "retrieval", Icon: Search, title: "Retrieval", body: bp.retrieval?.name ?? "Hybrid profile",
      detail: (bp.retrieval?.modes ?? []).map((m) => m.key).join(" + ") + (bp.retrieval?.top_k ? ` · top-k ${bp.retrieval.top_k}` : ""),
      why: path === "graph" ? "Blends vector recall with graph traversal for multi-hop reasoning." : "Hybrid retrieval mixes semantic and keyword recall.",
    },
    {
      key: "agent", Icon: Sparkles, title: "Agent",
      body: bp.agents?.name ?? "Reasoning agent",
      detail: `${bp.agents?.model ?? "model"}${agentDisabled ? " · disabled" : ""}`,
      why: agentDisabled ? "Ships disabled to verify retrieval quality first—no autonomous surprise." : "Orchestrates retrieval and reasoning into cited answers.",
    },
    {
      key: "governance", Icon: ShieldCheck, title: "Governance",
      body: bp.governance?.requires_approval ? "Approval required" : "Auto-publish",
      detail: bp.governance?.policy_note ?? "",
      why: bp.governance?.requires_approval ? "High-stakes outputs route to a human approval gate." : "Low-risk flows publish automatically with full lineage.",
    },
  ];

  return (
    <div className="w-full min-w-0">
      {/* Hide scrollbar on mobile but allow scrolling */}
      <div className="w-full overflow-x-auto hide-scrollbar pb-4">
        <div className="flex w-max items-stretch gap-3">
          {nodes.map((n, i) => (
            <div key={n.key} className="relative flex w-64 shrink-0 flex-col rounded-xl border border-border bg-panel p-4 shadow-sm">
              {i < nodes.length - 1 && (
                <div className="absolute -right-[19px] top-1/2 z-10 -translate-y-1/2 text-muted hidden sm:block">
                  <ArrowRight size={16} />
                </div>
              )}
              <div className="mb-3 flex items-center gap-3 border-b border-border pb-3">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-accent/20 bg-accent/10 text-accent">
                  <n.Icon size={16} />
                </span>
                <span className="text-sm font-bold">{n.title}</span>
              </div>
              <div className="flex-1">
                <span className="block text-xs font-semibold text-text">{n.body}</span>
                {n.detail && <span className="mt-1 block truncate text-[11px] text-muted" title={n.detail}>{n.detail}</span>}
              </div>
              <div className="mt-4 rounded bg-bg p-2 text-[10px] leading-relaxed text-muted border border-border/50">
                <strong className="text-text">Why: </strong>{n.why}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// --- Tabbed blueprint view -------------------------------------------------
type TabKey = "architecture" | "dag" | "ontology" | "graph" | "costs" | "risks";

function BlueprintTabs({ bp, rec, sessionId, sessionName, workspace, canEdit }: {
  bp: Blueprint; rec: Recommendation; sessionId: string; sessionName: string;
  workspace: string; canEdit: boolean;
}) {
  const [tab, setTab] = useState<TabKey>("architecture");
  const risks = useMemo(() => assessRisks(bp, rec), [bp, rec]);
  
  const tabs: { key: TabKey; label: string; Icon: typeof LayoutGrid; badge?: number }[] = [
    { key: "architecture", label: "Architecture", Icon: LayoutGrid },
    { key: "dag", label: "Pipeline DAG", Icon: GitBranch },
    { key: "ontology", label: "Semantic Model", Icon: Hexagon },
    { key: "graph", label: "Graph Explorer", Icon: Share2 },
    { key: "costs", label: "Cost Analysis", Icon: Coins },
    { key: "risks", label: "Risk Matrix", Icon: AlertTriangle, badge: risks.length || undefined },
  ];

  return (
    <div className="w-full min-w-0">
      {/* Scrollable Tabs */}
      <div className="mb-5 flex flex-nowrap overflow-x-auto hide-scrollbar gap-2 border-b border-border pb-px">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex shrink-0 items-center gap-2 border-b-2 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium transition-all ${
              tab === t.key
                ? "border-accent text-accent"
                : "border-transparent text-muted hover:border-border hover:text-text"
            }`}
          >
            <t.Icon size={16} /> {t.label}
            {t.badge != null && (
              <span className="grid h-5 min-w-5 place-items-center rounded-full bg-warn/10 px-1 text-[10px] font-bold text-warn border border-warn/20">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="w-full min-w-0 rounded-xl bg-bg/50 p-1 sm:p-2">
        {tab === "architecture" && <BlueprintCanvas bp={bp} rec={rec} />}
        {tab === "dag" && <>
          <DagFlow processors={bp.dag?.processors ?? []} name={bp.dag?.name} industry={bp.dag?.industry ?? bp.ontology?.domain} />
          <StudioDagEditor sessionId={sessionId} sessionName={sessionName} workspace={workspace}
            processors={bp.dag?.processors ?? []} canEdit={canEdit} />
        </>}
        {tab === "ontology" && <OntologyBlueprintView ontology={bp.ontology} />}
        {tab === "graph" && <TabPlaceholder text="A live knowledge-graph preview appears after you deploy this blueprint and ingest data. Open the graph explorer from the Ontology page." />}
        {tab === "costs" && <CostEstimate bp={bp} rec={rec} />}
        {tab === "risks" && <RiskAssessment risks={risks} />}
      </div>
    </div>
  );
}

function TabPlaceholder({ text }: { text: string }) {
  return (
    <div className="flex min-h-[200px] w-full items-center justify-center rounded-xl border-2 border-dashed border-border bg-panel p-6 text-center text-sm text-muted">
      {text}
    </div>
  );
}

/** Full-width pipeline DAG view with scroll prevention */
function DagFlow({ processors, name, industry }: { processors: string[]; name?: string; industry?: string }) {
  if (processors.length === 0) {
    return <TabPlaceholder text="This blueprint has no pipeline processors." />;
  }
  
  return (
    <div className="w-full min-w-0">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-text">Processing recommendation{industry ? ` · ${industry.replace(/_/g, " ")}` : ""}</h3>
          <p className="text-xs text-muted">The processor sequence follows source modality and goal. The selected industry changes the semantic model and eligible packs. This preview is not a deployed pipeline.</p>
        </div>
        {name && <span className="rounded bg-panel px-2 py-1 font-mono text-[10px] text-muted border border-border truncate max-w-full">{name}</span>}
      </div>
      
      {/* Outer wrapper establishes the bounded width and hides extreme overflows */}
      <div className="relative w-full overflow-hidden rounded-xl border border-border bg-panel p-4 sm:p-6 shadow-inner">
        {/* Background connector line (desktop mostly) */}
        <div className="absolute left-6 right-6 top-1/2 h-1 -translate-y-1/2 rounded bg-border/50 hidden md:block" aria-hidden />
        
        {/* Inner container provides horizontal scroll without breaking grid */}
        <div className="relative z-10 w-full overflow-x-auto hide-scrollbar">
          <div className="flex w-max min-w-full items-center justify-between gap-2 sm:gap-4">
            <Stage label="Source Systems" tone="muted" icon={Database} />
            
            {processors.map((p, i) => (
              <div key={`${p}-${i}`} className="flex items-center shrink-0">
                <div className="h-0.5 w-4 sm:w-8 bg-accent/40 hidden md:block" />
                <Stage label={p} tone="accent" icon={Workflow} />
              </div>
            ))}
            
            <div className="flex items-center shrink-0">
              <div className="h-0.5 w-4 sm:w-8 bg-border hidden md:block" />
              <Stage label="Index / Graph" tone="muted" icon={Search} />
            </div>
            
            <div className="flex items-center shrink-0">
              <div className="h-0.5 w-4 sm:w-8 bg-good/40 hidden md:block" />
              <Stage label="Agent Ready" tone="good" icon={Sparkles} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stage({ label, tone, icon: Icon }: { label: string; tone: "accent" | "muted" | "good", icon: LucideIcon }) {
  const cls =
    tone === "accent"
      ? "border-accent bg-accent/10 text-accent shadow-sm shadow-accent/10"
      : tone === "good"
        ? "border-good bg-good/10 text-good shadow-sm shadow-good/10"
        : "border-border bg-bg text-muted";
        
  return (
    <div className={`flex shrink-0 flex-col items-center justify-center gap-1.5 rounded-lg border-2 px-3 py-2 sm:px-4 text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-panel ${cls}`}>
      <Icon size={16} />
      {label}
    </div>
  );
}

// --- Cost estimate (client-side heuristic) ---------------------------------
function perRunCost(model?: string, disabled?: boolean): { usd: number; tier: string } {
  if (disabled || !model || /stub/i.test(model)) return { usd: 0, tier: "deterministic" };
  const m = model.toLowerCase();
  if (/(mini|haiku|flash|small|8b|7b)/.test(m)) return { usd: 0.002, tier: "economy" };
  if (/(opus|gpt-4(?!o)|70b|ultra|large)/.test(m)) return { usd: 0.04, tier: "premium" };
  return { usd: 0.011, tier: "standard" }; 
}

function CostEstimate({ bp, rec }: { bp: Blueprint; rec: Recommendation }) {
  const run = perRunCost(bp.agents?.model, bp.agents?.enabled === false);
  const entities = bp.ontology?.entity_types?.length ?? 0;
  const isGraph = (rec.decision_path ?? "") === "graph";
  const storageBand = entities > 25 || isGraph ? "med" : "low";
  const storageUsd = storageBand === "med" ? 40 : 12;
  const runsPerMo = 1000;
  const monthlyLlm = run.usd * runsPerMo;
  const rows = [
    { label: "Per Agent Execution (LLM)", value: run.usd === 0 ? "$0 (stub)" : `~$${run.usd.toFixed(3)} · ${run.tier}` },
    { label: "Retrieval Execution (vectors)", value: "~$0.0002" },
    { label: "Projected Compute @ 1k runs/mo", value: monthlyLlm === 0 ? "$0" : `~$${monthlyLlm.toFixed(0)}` },
    { label: "Storage Infrastructure", value: `~$${storageUsd}/mo (${storageBand})` },
  ];
  const monthlyTotal = monthlyLlm + storageUsd;
  
  return (
    <div className="w-full max-w-3xl min-w-0">
      <div className="overflow-x-auto rounded-xl border border-border shadow-sm">
        <div className="min-w-[400px]">
          {rows.map((r, i) => (
            <div key={r.label} className={`flex items-center justify-between px-4 sm:px-5 py-3 text-xs sm:text-sm ${i % 2 ? "bg-panel" : "bg-bg"}`}>
              <span className="font-medium text-text">{r.label}</span>
              <span className="font-mono text-muted">{r.value}</span>
            </div>
          ))}
          <div className="flex items-center justify-between border-t border-border bg-accent/5 px-4 sm:px-5 py-4 text-sm font-bold">
            <span className="text-text">Estimated Monthly Total</span>
            <span className="font-mono text-base sm:text-lg text-accent">~${monthlyTotal.toFixed(0)}/mo</span>
          </div>
        </div>
      </div>
      <p className="mt-3 text-xs text-muted max-w-2xl leading-relaxed">
        Projections derived from blueprint configuration at an assumed 1,000 runs/month. LLM calls bill against your BYOK credential — utilize the <span className="font-mono text-text">stub</span> model for a $0 deterministic baseline during sandbox testing.
      </p>
    </div>
  );
}

// --- Risk assessment (client-side heuristic) -------------------------------
interface Risk { id: string; severity: "high" | "med" | "low"; title: string; mitigation: string }

function assessRisks(bp: Blueprint, rec: Recommendation): Risk[] {
  const out: Risk[] = [];
  const path = rec.decision_path ?? "";
  const agentOn = bp.agents?.enabled !== false;
  const approval = bp.governance?.requires_approval === true;
  const entities = bp.ontology?.entity_types?.length ?? 0;
  const run = perRunCost(bp.agents?.model, bp.agents?.enabled === false);

  if (agentOn && !approval) {
    out.push({
      id: "autonomy", severity: path === "action" ? "high" : "med",
      title: "Agent autonomy without human-in-the-loop approval gate",
      mitigation: "Enforce “Approval required” in Governance policies, or deploy disabled until retrieval metrics are verified.",
    });
  }
  if (!bp.evals?.dataset_name) {
    out.push({
      id: "evals", severity: "med",
      title: "Missing evaluation dataset — qualitative regressions undetected",
      mitigation: "Seed a golden Q&A dataset in Evals to baseline performance before opening traffic.",
    });
  }
  if (path === "action") {
    out.push({
      id: "blast-radius", severity: "high",
      title: "Closed-loop automation executes physical side-effects",
      mitigation: "Scope tool RBAC tightly and rehearse in an isolated sandbox environment before production deployment.",
    });
  }
  if (entities > 25) {
    out.push({
      id: "model-complexity", severity: "low",
      title: `Extensive ontology (${entities} entities) increases maintenance overhead`,
      mitigation: "Deploy core operational entities first; iteratively expand schema based on query demand.",
    });
  }
  if (run.tier === "premium") {
    out.push({
      id: "cost", severity: "med",
      title: "Premium LLM tier accelerates operational burn rate",
      mitigation: "Benchmark standard/economy models against the eval dataset to justify premium compute costs.",
    });
  }
  return out;
}

function RiskAssessment({ risks }: { risks: Risk[] }) {
  if (risks.length === 0) {
    return (
      <div className="flex w-full max-w-3xl items-center gap-3 rounded-xl border border-good/30 bg-good/5 p-4 sm:p-5 text-sm font-medium text-good">
        <CheckCircle2 size={20} className="shrink-0" /> 
        No elevated architectural risks detected. Governance, evals, and agent configurations are aligned with best practices.
      </div>
    );
  }
  const tone = { high: "border-bad/30 bg-bad/5 text-bad", med: "border-warn/30 bg-warn/5 text-warn", low: "border-border bg-panel text-text" } as const;
  
  return (
    <div className="grid w-full gap-3 md:grid-cols-2 min-w-0">
      {risks.map((r) => (
        <div key={r.id} className={`flex flex-col rounded-xl border p-4 shadow-sm min-w-0 ${tone[r.severity]}`}>
          <div className="mb-2 flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 text-sm font-bold min-w-0">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span className="truncate whitespace-normal leading-snug">{r.title}</span>
            </div>
            <span className="shrink-0 rounded-md border border-current px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
              {r.severity}
            </span>
          </div>
          <div className="mt-auto pt-3 text-xs leading-relaxed text-muted border-t border-current/10">
            <strong className="font-bold text-text">Mitigation strategy: </strong>{r.mitigation}
          </div>
        </div>
      ))}
    </div>
  );
}

function DeployedRefs({ refs, sessionId }: { refs: Record<string, string | null>; sessionId: string }) {
  const links: Record<string, string> = {
    ontology_version: "/ontology",
    retrieval_profile: "/retrieval",
    agent: "/agents",
  };
  const [tab, setTab] = useState<"resources" | "connect" | "graph">("resources");
  const ontologyVersionId = refs.ontology_version;

  return (
    <div className="w-full space-y-4 rounded-xl border border-good/30 bg-good/5 p-4 sm:p-5 min-w-0">
      <div className="flex items-center gap-2 text-base font-bold text-good">
        <CheckCircle2 size={20} className="shrink-0" /> Provisioning Complete
      </div>

      {/* G3/G8 — tabbed deployed view: resources · connect via API · live graph */}
      <div className="inline-flex rounded-md border border-border bg-panel p-0.5 text-xs">
        {([
          ["resources", "Resources"],
          ["connect", "Connect via API"],
          ...(ontologyVersionId ? [["graph", "Graph"] as const] : []),
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={"rounded px-3 py-1 font-medium " + (tab === key ? "bg-accent/15 text-accent" : "text-muted hover:text-fg")}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "resources" && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(refs).map(([kind, id]) => (
            <div key={kind} className="flex flex-col justify-between rounded-lg border border-border bg-panel p-4 shadow-sm min-w-0">
              <span className="mb-3 text-[11px] font-bold uppercase tracking-wider text-muted truncate">{kind.replace(/_/g, " ")}</span>
              {id ? (
                <Link to={links[kind] ?? "/dashboard"} className="inline-flex w-fit items-center gap-1.5 text-sm font-bold text-accent transition-colors hover:text-accent/80">
                  View Resource <ArrowRight size={14} />
                </Link>
              ) : <span className="text-xs italic text-muted">Skipped in this architecture</span>}
            </div>
          ))}
        </div>
      )}

      {tab === "connect" && <ConnectPanel sessionId={sessionId} />}

      {tab === "graph" && ontologyVersionId && (
        <DeployedGraph versionId={ontologyVersionId} />
      )}
    </div>
  );
}

// -------------------------------------------------------- G3: Connect via API
interface EndpointBlock {
  method?: string;
  methods?: string[];
  url?: string;
  template?: string;
  description?: string;
  agent_id?: string;
  enabled?: boolean | null;
  note?: string | null;
}
interface ConnectInfo {
  workspace_id: string;
  status: string;
  deployed: boolean;
  auth: { scheme: string; header: string; obtain: string };
  resources: Record<string, string | null>;
  endpoints: Record<string, EndpointBlock | null>;
  curl_example: string;
}

function ConnectPanel({ sessionId }: { sessionId: string }) {
  const info = useQuery<ConnectInfo>({
    queryKey: ["studio-endpoints", sessionId],
    queryFn: () => rawFetch(`/studio/sessions/${sessionId}/endpoints/`),
  });
  const [copied, setCopied] = useState<string | null>(null);

  function copy(key: string, text: string) {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
    });
  }

  if (info.isLoading) return <div className="text-sm text-muted">Loading endpoints…</div>;
  if (info.isError) return <ErrorBox error={info.error} />;
  const data = info.data;
  if (!data) return null;

  const rows: { key: string; label: string; url: string; extra?: string }[] = [];
  for (const [key, block] of Object.entries(data.endpoints)) {
    if (!block) continue;
    const url = block.url || block.template;
    if (!url) continue;
    const verb = block.method || (block.methods ? block.methods.join("/") : "GET");
    rows.push({ key, label: key.replace(/_/g, " "), url, extra: `${verb}${block.note ? ` · ${block.note}` : ""}` });
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-border bg-panel p-3 text-xs">
        <div className="mb-1 flex items-center gap-2 font-semibold text-fg">
          <Plug size={13} className="text-accent" /> Authentication
        </div>
        <p className="text-muted">
          Send <code className="font-mono">{data.auth.header}</code>. Obtain a token at{" "}
          <code className="font-mono">{data.auth.obtain}</code>.
        </p>
      </div>

      {rows.map((r) => (
        <div key={r.key} className="rounded-lg border border-border bg-panel p-3">
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted">{r.label}</span>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-[11px] text-accent hover:text-accent/80"
              onClick={() => copy(r.key, r.url)}
            >
              {copied === r.key ? <Check size={12} /> : <Copy size={12} />}
              {copied === r.key ? "Copied" : "Copy"}
            </button>
          </div>
          <code className="block break-all font-mono text-[11px] text-fg">{r.url}</code>
          {r.extra && <div className="mt-1 text-[10px] text-muted">{r.extra}</div>}
        </div>
      ))}

      <div className="rounded-lg border border-border bg-black/40 p-3">
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-muted">curl example</span>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[11px] text-accent hover:text-accent/80"
            onClick={() => copy("curl", data.curl_example)}
          >
            {copied === "curl" ? <Check size={12} /> : <Copy size={12} />}
            {copied === "curl" ? "Copied" : "Copy"}
          </button>
        </div>
        <code className="block break-all font-mono text-[11px] text-good">{data.curl_example}</code>
      </div>
    </div>
  );
}

// ----------------------------------------------- G8: live deployed graph view
function DeployedGraph({ versionId }: { versionId: string }) {
  const graph = useQuery<OntologyGraph>({
    queryKey: ["studio-deployed-graph", versionId],
    queryFn: () => rawFetch(`/ontology/versions/${versionId}/graph/`),
  });
  if (graph.isLoading) return <div className="py-10 text-center text-sm text-muted">Loading graph…</div>;
  if (graph.isError) return <ErrorBox error={graph.error} />;
  if (!graph.data || graph.data.nodes.length === 0) {
    return <div className="py-10 text-center text-sm text-muted">No classes in the deployed ontology yet.</div>;
  }
  return (
    <div className="rounded-lg border border-border bg-panel p-2">
      <OntologyGraphExplorer graph={graph.data} />
    </div>
  );
}
